import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { InputText } from "../../../components/InputText";
import { api } from "../../../services/api";
import { Bot, Save, Loader2, Plus, Trash2, X } from "lucide-react";

type TipoConfig = "filas" | "patrulhas" | "patrulhas_sessao" | "rm" | "rmo" | "promocoes" | "status" | "meu_status" | "analise_promocoes";

interface DiscordConfiguracaoCanal {
  id?: string;
  id_servidor_discord: string | null;
  id_canal_discord: string | null;
  id_cargo_discord: string | null;
  ativo: boolean;
}

interface DiscordConfiguracao {
  tipo: TipoConfig;
  ativo: boolean;
  canais: DiscordConfiguracaoCanal[];
}

const MODULOS: { tipo: TipoConfig; label: string; descricao: string }[] = [
  {
    tipo: "filas",
    label: "Filas de Treinamento",
    descricao: "Notificações sobre responsáveis de filas: iniciar, pegar, pausar, retomar e finalizar.",
  },
  {
    tipo: "patrulhas_sessao",
    label: "Patrulhas — Sessão e Funções",
    descricao: "Notificações de abertura, pausa, retomada e finalização de sessão, e assunção de funções (supervisor, monitor, auxiliar).",
  },
  {
    tipo: "patrulhas",
    label: "Patrulhas — Concluídas",
    descricao: "Notificações de patrulhas concluídas, com o civil que trouxe o patrulheiro.",
  },
  {
    tipo: "rm",
    label: "Relatório de Movimento (RM)",
    descricao: "Notificações sobre o responsável do RM.",
  },
  {
    tipo: "rmo",
    label: "Relatório de Movimento Oficial (RMO)",
    descricao: "Notificações sobre o responsável do RMO.",
  },
  {
    tipo: "promocoes",
    label: "Promoções / Rebaixamentos",
    descricao: "Notificações de promoções e rebaixamentos de patente de militares.",
  },
  {
    tipo: "status",
    label: "Alterações de Status",
    descricao: "Notificações de alterações de status de militares para demitido.",
  },
  {
    tipo: "meu_status",
    label: "Meu Status",
    descricao: "Notificações quando um militar entra ou sai de atividade (com o tipo).",
  },
  {
    tipo: "analise_promocoes",
    label: "Análise de Promoções",
    descricao: "Canal para receber as análises de promoção enviadas manualmente a partir do dashboard de desempenho de praças.",
  },
];

export function Discord() {
  const [configs, setConfigs] = useState<Record<TipoConfig, DiscordConfiguracao>>({} as Record<TipoConfig, DiscordConfiguracao>);
  const [loading, setLoading] = useState(true);
  const [savingCanal, setSavingCanal] = useState<string | null>(null);
  const [deletingCanal, setDeletingCanal] = useState<string | null>(null);
  const [editingCanal, setEditingCanal] = useState<Record<TipoConfig, number | null>>({} as Record<TipoConfig, number | null>);
  const [novoCanal, setNovoCanal] = useState<Record<TipoConfig, DiscordConfiguracaoCanal>>({} as Record<TipoConfig, DiscordConfiguracaoCanal>);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get<DiscordConfiguracao[]>("/discord/configuracoes");
      const updated = {} as Record<TipoConfig, DiscordConfiguracao>;

      for (const item of response.data) {
        updated[item.tipo] = item;
      }

      // Inicializar novos canais vazios
      const novosCanaisInit = {} as Record<TipoConfig, DiscordConfiguracaoCanal>;
      const editingInit = {} as Record<TipoConfig, number | null>;

      MODULOS.forEach(({ tipo }) => {
        if (!updated[tipo]) {
          updated[tipo] = { tipo, ativo: true, canais: [] };
        }
        novosCanaisInit[tipo] = {
          id_servidor_discord: null,
          id_canal_discord: null,
          id_cargo_discord: null,
          ativo: true,
        };
        editingInit[tipo] = null;
      });

      setConfigs(updated);
      setNovoCanal(novosCanaisInit);
      setEditingCanal(editingInit);
    } catch {
      enqueueSnackbar("Erro ao carregar configurações do Discord", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarCanal = async (tipo: TipoConfig) => {
    const canal = novoCanal[tipo];

    if (!canal.id_canal_discord) {
      enqueueSnackbar("ID do Canal é obrigatório", { variant: "error" });
      return;
    }

    try {
      setSavingCanal(tipo);
      await api.post(`/discord/configuracoes/${tipo}/canais`, canal);
      enqueueSnackbar("Canal adicionado com sucesso!", { variant: "success" });

      // Resetar formulário
      setNovoCanal((prev) => ({
        ...prev,
        [tipo]: {
          id_servidor_discord: null,
          id_canal_discord: null,
          id_cargo_discord: null,
          ativo: true,
        },
      }));

      setEditingCanal((prev) => ({ ...prev, [tipo]: null }));
      await loadConfigs();
    } catch {
      enqueueSnackbar("Erro ao adicionar canal", { variant: "error" });
    } finally {
      setSavingCanal(null);
    }
  };

  const handleRemoverCanal = async (tipo: TipoConfig, canalId: string) => {
    try {
      setDeletingCanal(canalId);
      await api.delete(`/discord/configuracoes/canais/${canalId}`);
      enqueueSnackbar("Canal removido com sucesso!", { variant: "success" });
      await loadConfigs();
    } catch {
      enqueueSnackbar("Erro ao remover canal", { variant: "error" });
    } finally {
      setDeletingCanal(null);
    }
  };

  const handleChangeNovoCanal = (tipo: TipoConfig, field: keyof DiscordConfiguracaoCanal, value: string) => {
    setNovoCanal((prev) => ({
      ...prev,
      [tipo]: { ...prev[tipo], [field]: value || null },
    }));
  };

  const toggleFormNovoCanal = (tipo: TipoConfig) => {
    setEditingCanal((prev) => ({
      ...prev,
      [tipo]: prev[tipo] === -1 ? null : -1,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bot className="w-6 h-6 text-indigo-500" />
        <div>
          <h1 className="text-2xl font-bold">Integração Discord</h1>
          <p className="text-sm text-muted-foreground">
            Configure múltiplos canais do Discord para receber notificações automáticas de cada módulo.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {MODULOS.map(({ tipo, label, descricao }) => (
          <Card key={tipo}>
            <CardHeader>
              <CardTitle className="text-base">{label}</CardTitle>
              <CardDescription>{descricao}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Lista de canais configurados */}
              {configs[tipo]?.canais && configs[tipo].canais.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-muted-foreground">Canais Configurados</Label>
                  {configs[tipo].canais.map((canal, index) => (
                    <div
                      key={canal.id || index}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg text-sm"
                    >
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Canal:</span>
                          <code className="text-xs bg-background px-2 py-0.5 rounded">
                            {canal.id_canal_discord || "—"}
                          </code>
                        </div>
                        {canal.id_servidor_discord && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Servidor:</span>
                            <code className="bg-background px-2 py-0.5 rounded">
                              {canal.id_servidor_discord}
                            </code>
                          </div>
                        )}
                        {canal.id_cargo_discord && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Cargo:</span>
                            <code className="bg-background px-2 py-0.5 rounded">
                              {canal.id_cargo_discord}
                            </code>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => canal.id && handleRemoverCanal(tipo, canal.id)}
                        disabled={deletingCanal === canal.id}
                        className="ml-2"
                      >
                        {deletingCanal === canal.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-destructive" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Formulário para adicionar novo canal */}
              {editingCanal[tipo] === -1 ? (
                <div className="space-y-3 p-4 border border-dashed rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Novo Canal</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleFormNovoCanal(tipo)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`novo-servidor-${tipo}`} className="text-xs">
                      ID do Servidor (opcional)
                    </Label>
                    <InputText
                      name={`novo-servidor-${tipo}`}
                      placeholder="Ex: 123456789012345678"
                      value={novoCanal[tipo]?.id_servidor_discord ?? ""}
                      onChange={(e) => handleChangeNovoCanal(tipo, "id_servidor_discord", e.target.value)}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`novo-canal-${tipo}`} className="text-xs">
                      ID do Canal *
                    </Label>
                    <InputText
                      name={`novo-canal-${tipo}`}
                      placeholder="Ex: 987654321098765432"
                      value={novoCanal[tipo]?.id_canal_discord ?? ""}
                      onChange={(e) => handleChangeNovoCanal(tipo, "id_canal_discord", e.target.value)}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`novo-cargo-${tipo}`} className="text-xs">
                      ID do Cargo a Mencionar (opcional)
                    </Label>
                    <InputText
                      name={`novo-cargo-${tipo}`}
                      placeholder="Ex: 112233445566778899"
                      value={novoCanal[tipo]?.id_cargo_discord ?? ""}
                      onChange={(e) => handleChangeNovoCanal(tipo, "id_cargo_discord", e.target.value)}
                    />
                  </div>

                  <Button
                    className="w-full"
                    size="sm"
                    onClick={() => handleAdicionarCanal(tipo)}
                    disabled={savingCanal === tipo}
                  >
                    {savingCanal === tipo ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Adicionar Canal
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  size="sm"
                  onClick={() => toggleFormNovoCanal(tipo)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Canal
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
